import React, { useState, useEffect } from 'react';
import './EcoNews.css';

interface NewsItem {
  title: string;
  description: string;
  link: string;
  pubDate: string;
  image?: string;
}

// Fallback news items for when the API is unavailable
const fallbackNews: NewsItem[] = [
  {
    title: "Amazing Ocean Cleanup Project",
    description: "Scientists have discovered a new way to clean up plastic from our oceans using special nets that don't harm sea creatures. This could help save many marine animals!",
    link: "https://www.worldwildlife.org/initiatives/oceans",
    pubDate: new Date().toLocaleDateString(),
    image: "https://images.unsplash.com/photo-1526951521990-620dc14c214b?w=800&auto=format&fit=crop&q=60"
  },
  {
    title: "Kids Plant 1000 Trees",
    description: "A group of school children planted 1000 trees in their local park! They learned how trees help clean the air and provide homes for animals.",
    link: "https://www.worldwildlife.org/initiatives/forests",
    pubDate: new Date().toLocaleDateString(),
    image: "https://images.unsplash.com/photo-1513836279014-a89f7a76ae86?w=800&auto=format&fit=crop&q=60"
  },
  {
    title: "Solar Power for Schools",
    description: "More schools are using solar panels to get their energy from the sun! This helps reduce pollution and teaches kids about renewable energy.",
    link: "https://www.worldwildlife.org/initiatives/climate",
    pubDate: new Date().toLocaleDateString(),
    image: "https://images.unsplash.com/photo-1509391366360-2e959784a276?w=800&auto=format&fit=crop&q=60"
  },
  {
    title: "New Animal Species Found",
    description: "Scientists discovered a new type of frog in the rainforest! It's bright green and helps keep the forest healthy by eating harmful insects.",
    link: "https://www.worldwildlife.org/species",
    pubDate: new Date().toLocaleDateString(),
    image: "https://images.unsplash.com/photo-1593069567131-53a0614dde1d?w=800&auto=format&fit=crop&q=60"
  },
  {
    title: "Recycling Champions",
    description: "A group of kids started a recycling program at their school. They collect paper, plastic, and cans to help protect the environment!",
    link: "https://www.worldwildlife.org/initiatives/waste",
    pubDate: new Date().toLocaleDateString(),
    image: "https://images.unsplash.com/photo-1610093674388-cee0337f2684?w=800&auto=format&fit=crop&q=60"
  },
  {
    title: "Save the Bees!",
    description: "Kids are planting special flowers to help save the bees. Bees are important because they help plants grow and make honey!",
    link: "https://www.worldwildlife.org/initiatives/wildlife-conservation",
    pubDate: new Date().toLocaleDateString(),
    image: "https://images.unsplash.com/photo-1723779342799-1298abf67957?w=800&auto=format&fit=crop&q=60"
  }
];

const EcoNews: React.FC = () => {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        // Try to fetch from WWF news feed
        const response = await fetch('https://api.rss2json.com/v1/api.json?rss_url=https://www.worldwildlife.org/stories/rss.xml');
        const data = await response.json();
        
        if (data.status === 'ok' && data.items && data.items.length > 0) {
          // Filter and transform the news items
          const filteredNews = data.items
            .filter((item: any) => 
              item.title.toLowerCase().includes('animal') ||
              item.title.toLowerCase().includes('nature') ||
              item.title.toLowerCase().includes('environment') ||
              item.title.toLowerCase().includes('planet') ||
              item.title.toLowerCase().includes('wildlife')
            )
            .map((item: any) => ({
              title: item.title,
              description: item.description,
              link: item.link,
              pubDate: new Date(item.pubDate).toLocaleDateString(),
              image: item.thumbnail
            }))
            .slice(0, 6); // Limit to 6 items
          
          if (filteredNews.length > 0) {
            setNews(filteredNews);
          } else {
            // If no filtered news, use fallback
            setNews(fallbackNews);
          }
        } else {
          // If API fails, use fallback
          setNews(fallbackNews);
        }
      } catch (err) {
        console.error('Error fetching news:', err);
        // Use fallback news on error
        setNews(fallbackNews);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, []);

  if (loading) {
    return (
      <div className="eco-news-container">
        <div className="loading-spinner">
          <span>🌱</span>
          <p>Loading eco news...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="eco-news-container">
      <h2>Eco News for Kids</h2>
      <div className="news-grid">
        {news.map((item, index) => (
          <article key={index} className="news-card">
            {item.image && (
              <div className="news-image">
                <img src={item.image} alt={item.title} />
              </div>
            )}
            <div className="news-content">
              <h3>{item.title}</h3>
              <p className="news-date">{item.pubDate}</p>
              <p className="news-description">{item.description}</p>
              <a 
                href={item.link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="read-more"
              >
                Read More
              </a>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};

export default EcoNews; 